import {
  actionCreators as fileUploadActions,
  FileUploadActionTypes,
  IFileUploadSuccessAction,
} from '@app/components/FileUpload/modules/fileUpload'
import { IAttachment } from '@app/models/Attachment'
import { Form, IForm } from '@app/models/Form'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum FormsScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IFormsListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IForm>
  readonly scope: FormsScope
  readonly totalItems: number
}

const FormsListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Form>(),
  scope: FormsScope.All,
  totalItems: 0,
})

export class FormsListState extends FormsListStateRecord
  implements IFormsListState {
  public static fromJS(initialState?: Partial<IFormsListState>) {
    if (!initialState) {
      return new FormsListState()
    }

    const { items, ...rest } = initialState
    return new FormsListState({
      items: items ? List(items.map((i) => Form.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Form>
  public readonly scope: FormsScope
  public readonly sortOrder: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<FormsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  EditForm = 'FORMS_LIST::EDIT_FORM',
  LoadForms = 'FORMS_LIST::LOAD_FORMS',
  LoadFormsFailure = 'FORMS_LIST::LOAD_FORMS_FAILURE',
  LoadFormsRequest = 'FORMS_LIST::LOAD_FORMS_REQUEST',
  LoadFormsSuccess = 'FORMS_LIST::LOAD_FORMS_SUCCESS',
  RemoveForm = 'FORMS_LIST::REMOVE_FORM',
  SetAttachment = 'FORMS_LIST::SET_ATTACHMENT',
  SetFilterText = 'FORMS_LIST::SET_FILTER_TEXT',
  SetScope = 'FORMS_LIST::SET_SCOPE',
}

interface IEditFormAction {
  form: IForm
  type: ActionTypes.EditForm
  value: IAttachment
}

interface ILoadFormsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadForms
}

interface ILoadFormsFailureAction {
  error: string
  type: ActionTypes.LoadFormsFailure
}

interface ILoadFormsRequestAction {
  type: ActionTypes.LoadFormsRequest
}

interface ILoadFormsSuccessAction {
  append: boolean
  items: List<IForm>
  totalItems: number
  type: ActionTypes.LoadFormsSuccess
}

interface IRemoveFormAction {
  id: string
  type: ActionTypes.RemoveForm
}

interface ISetAttachmentAction {
  id: string
  type: ActionTypes.SetAttachment
  value: IAttachment
}

interface ISetFilterTextAction {
  filter: string
  type: ActionTypes.SetFilterText
}

interface ISetScopeAction {
  scope: FormsScope
  type: ActionTypes.SetScope
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IEditFormAction
  | ILoadFormsFailureAction
  | ILoadFormsRequestAction
  | ILoadFormsSuccessAction
  | ISetAttachmentAction
  | ISetFilterTextAction
  | ISetScopeAction

// -------------------------
// ACTION CREATORS
// -------------------------
function editForm(form: IForm, value: IAttachment): IEditFormAction {
  return {
    form,
    type: ActionTypes.EditForm,
    value,
  }
}

function loadForms(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadFormsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadForms,
  }
}

function loadFormsFailure(error: string): ILoadFormsFailureAction {
  return {
    error,
    type: ActionTypes.LoadFormsFailure,
  }
}

function loadFormsRequest(): ILoadFormsRequestAction {
  return {
    type: ActionTypes.LoadFormsRequest,
  }
}

function loadFormsSuccess(
  items: List<IForm>,
  append: boolean,
  totalItems: number,
): ILoadFormsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadFormsSuccess,
  }
}

function removeForm(id: string): IRemoveFormAction {
  return {
    id,
    type: ActionTypes.RemoveForm,
  }
}

function setAttachment(value: IAttachment, id: string): ISetAttachmentAction {
  return {
    id,
    type: ActionTypes.SetAttachment,
    value,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ActionTypes.SetFilterText,
  }
}

function setScope(scope: FormsScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

export const actionCreators = {
  editForm,
  loadForms,
  removeForm,
  setAttachment,
  setFilterText,
  setScope,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<FormsListState> = (
  state: FormsListState = new FormsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.EditForm:
      const formIndexToEdit = state.items.findIndex((item: any) => {
        return item.id === action.form.id
      })

      return state.setIn(
        ['items', formIndexToEdit, 'file'],
        action.value,
      ) as FormsListState

    case ActionTypes.LoadFormsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as FormsListState

    case ActionTypes.LoadFormsRequest:
      return state.set('isLoading', true) as FormsListState

    case ActionTypes.LoadFormsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Form>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as FormsListState

    case ActionTypes.SetAttachment:
      const aIndex = state.items.findIndex((item: any) => {
        return item.id === action.id
      })
      return state.setIn(
        ['items', aIndex, 'file'],
        action.value,
      ) as FormsListState

    case ActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as FormsListState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as FormsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* createNewFormSaga(action: IFileUploadSuccessAction) {
  try {
    const forms = yield select(selectForms())

    if (forms && action.file.key === 'forms-file-upload') {
      const baseUrl = '/api/Forms/Create'

      const opts = {
        body: JSON.stringify({ fileId: action.attachment.id }),
        method: RequestMethod.POST,
      }

      const result = yield makeAuthenticatedRequest(baseUrl, opts)

      if (result.success === true) {
        yield put(fileUploadActions.removeFileUploadItem(action.file.key, action.file.id))
        yield put(
          successToast(
            'Success',
            `${action.attachment.fileName} was successfully added`,
          ),
        )
        yield put(loadForms(0, 20, false))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadFormsSaga(action: ILoadFormsAction) {
  try {
    yield put(loadFormsRequest())

    const currentState: FormsListState = yield select(
      (state: ApplicationState) => state.formsList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    query.push(`sort=Most`)

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    let baseUrl = '/api/Forms'

    if (currentState.scope === FormsScope.All) {
      baseUrl = '/api/Forms/All'
    }

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const forms: List<IForm> = result.results.map((c: any) => {
        return Form.fromJS(c)
      })

      yield put(loadFormsSuccess(forms, action.append, 0))
    } else {
      yield put(loadFormsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadFormsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* removeFormSaga(action: IRemoveFormAction) {
  try {
    const forms = yield select(selectForms())

    if (forms) {
      const baseUrl = `/api/Forms/${action.id}`

      const opts = {
        method: RequestMethod.DELETE,
      }

      const result = yield makeAuthenticatedRequest(baseUrl, opts)

      if (result.success === true) {
        yield put(successToast('Success', `Form was successfully removed`))
        yield put(loadForms(0, 20, false))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchFileUpload() {
  yield takeLatest(FileUploadActionTypes.FileUploadSuccess, createNewFormSaga)
}

function* watchLoadForms() {
  yield takeLatest(ActionTypes.LoadForms, loadFormsSaga)
}

function* watchRemoveForm() {
  yield takeLatest(ActionTypes.RemoveForm, removeFormSaga)
}

export function* formsListRootSaga() {
  yield fork(watchLoadForms)
  yield fork(watchFileUpload)
  yield fork(watchRemoveForm)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): FormsListState =>
  state.formsList

export const selectForms = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
